import React, { useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import moment from 'moment';

import {
  addExpense,
  attachFiles,
  cleanPayout,
  deferLine,
  deleteOwnExpense,
  excludeLine,
  getPayout,
  removeAttachment,
  restoreLine,
  stopRecurringLine,
} from '@/store/actions/payout';
import { selectPayout } from '@/store/selectors/payout';
import { selectRole } from '@/store/selectors/user';
import { Currency, ExpenseAttachment, PayoutLine } from '@/store/reducers/payout';

import useLanguage from '@/components/common/hooks/useLanguage';
import useToggle from '@/components/common/hooks/useToggle';
import BackIcon from '@/components/common/icons/BackIcon';
import Loader from '@/components/common/Loader';
import AddExpense from '@/components/payout/AddExpense';
import ExpenseLine from '@/components/payout/ExpenseLine';
import { formatMoney, formatMoneyWithUah } from '@/utils/money';

interface Props extends RouteComponentProps<{ userId?: string }> {}

/**
 * Serves both `/payout` (mine) and `/users/payout/:userId` (an admin looking at
 * someone else's). The difference that matters is not the route but the
 * response: for a non-admin the server sends expense lines and an expenses-only
 * subtotal, and no salary at all, so there is nothing here to hide.
 */
const Payout: React.FC<Props> = ({ match, history }) => {
  const userId = match.params.userId ? +match.params.userId : undefined;
  const dispatch = useDispatch();
  const payoutData = useSelector(selectPayout);
  const role = useSelector(selectRole);
  const [{ payout }] = useLanguage();
  const [isAdding, toggleIsAdding] = useToggle();
  const [period] = useState(moment().format('YYYY-MM'));

  const isAdmin = role === 'admin';
  const target = { userId, period };

  // A layout effect, so the stored copy renders before the first paint -- see
  // store/responses.ts.
  useLayoutEffect(() => {
    dispatch(getPayout({ userId }));

    return () => {
      dispatch(cleanPayout());
    };
  }, [userId]);

  if (!payoutData) return <Loader />;

  const onAdd = (
    body: { title: string, amount: number, currency?: Currency, isRecurring: boolean },
    files: File[],
  ) => {
    dispatch(addExpense(body, target, files));
  };

  const handlers = {
    onDefer: (line: PayoutLine) => dispatch(deferLine(line, payoutData.period, target)),
    onExclude: (line: PayoutLine) => dispatch(excludeLine(line, payoutData.period, target)),
    onRestore: (line: PayoutLine) => dispatch(restoreLine(line, target)),
    onStopRecurring: (line: PayoutLine) => (
      dispatch(stopRecurringLine(line, payoutData.period, target))
    ),
    onDelete: (line: PayoutLine) => (
      line.expenseId ? dispatch(deleteOwnExpense(line.expenseId, target)) : undefined
    ),
    onAttach: (line: PayoutLine, files: File[]) => (
      dispatch(attachFiles(line, files, payoutData.period, target))
    ),
    onRemoveAttachment: (attachment: ExpenseAttachment) => (
      dispatch(removeAttachment(attachment, target))
    ),
  };

  return (
    <>
      <Header>
        <HeaderWrap>
          <TopButton onClick={history.goBack}>
            <BackIcon />
          </TopButton>
          <HeaderText>
            {payoutData.user ? payoutData.user.name : payout.next_payout}
          </HeaderText>
        </HeaderWrap>
      </Header>

      <Main className="scrollbar">
        <Summary>
          <SummaryPeriod>
            {moment(payoutData.period, 'YYYY-MM').format('MMMM YYYY')}
          </SummaryPeriod>
          <SummaryValue>
            {formatMoneyWithUah(
              payoutData.total !== undefined
                ? payoutData.total
                : payoutData.expensesTotal,
              payoutData.expensesTotalUah,
            )}
          </SummaryValue>
          {payoutData.salary !== undefined && (
            <Breakdown>
              <BreakdownRow>
                <span>{payout.salary}</span>
                <span>{formatMoney(payoutData.salary)}</span>
              </BreakdownRow>
              <BreakdownRow>
                <span>{payout.expenses}</span>
                <span>
                  {formatMoneyWithUah(payoutData.expensesTotal, payoutData.expensesTotalUah)}
                </span>
              </BreakdownRow>
            </Breakdown>
          )}
        </Summary>

        <SectionHead>
          <Label>{payout.expenses}</Label>
          <AddButton onClick={() => toggleIsAdding(true)}>
            {payout.add_expense}
          </AddButton>
        </SectionHead>

        {payoutData.lines.length === 0 && <Empty>{payout.no_expenses}</Empty>}
        {payoutData.lines.map((line) => (
          <ExpenseLine
            key={line.ref}
            line={line}
            isAdmin={isAdmin}
            {...handlers}
          />
        ))}

        {payoutData.lines.length > 0 && (
          <TotalRow>
            <span>{payout.total}</span>
            <strong>
              {formatMoneyWithUah(payoutData.expensesTotal, payoutData.expensesTotalUah)}
            </strong>
          </TotalRow>
        )}
      </Main>

      {isAdding && (
        <AddExpense onAdd={onAdd} onClose={() => toggleIsAdding(false)} />
      )}
    </>
  );
};

const Header = styled.header`
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.17);
  background-color: #ffffff;
  height: 72px;
  position: sticky;
  z-index: 100;
  width: 100%;
  top: 0;
`;

const HeaderWrap = styled.div`
  width: 100%;
  height: 72px;
  display: flex;
  align-items: center;
  padding: 12px 16px;
`;

const HeaderText = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #21272e;
  margin-left: 24px;
`;

const TopButton = styled.div`
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Main = styled.div`
  padding: 16px 16px 80px;
  background: #ffffff;
  height: 100%;
  overflow-y: auto;
  flex: 1;
`;

const Summary = styled.div`
  border-radius: 6px;
  border: solid 1px #dae1e8;
  padding: 16px;
  text-align: center;
`;

const SummaryPeriod = styled.div`
  text-transform: uppercase;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.3px;
  color: #909599;
`;

const SummaryValue = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: #21272e;
  margin-top: 6px;
`;

const Breakdown = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: solid 1px #eceff2;
`;

const BreakdownRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #787c80;
  padding: 3px 0;
`;

const SectionHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 24px 0 4px;
`;

const Label = styled.div`
  text-transform: uppercase;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.3px;
  color: #909599;
`;

const AddButton = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #fb7e14;
  cursor: pointer;
`;

const Empty = styled.div`
  font-size: 14px;
  color: #909599;
  padding: 12px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #21272e;
  padding: 14px 0;
`;

export default Payout;
